import React, { forwardRef } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation } from "react-router-dom"

import {
  CustomIcon,
  FriendsIcon,
  GamesIcon,
  HomeIcon,
  UserIcon,
} from "@/shared/assets/icons"
import { Separator } from "@/shared/ui/separator"
import { useDimension } from "@/shared/store/dimensions-store"
import { usePostHog } from "posthog-js/react"
import { AnalyticsEvent } from "@/shared/typings/analyticsEvents"

const menuItems = [
  {
    title: "quests",
    link: "/quests",
    icon: <GamesIcon />,
  },
  {
    title: "customs",
    link: "/customs",
    icon: <CustomIcon />,
  },
  {
    title: "home",
    link: "/",
    icon: <HomeIcon className="h-[30px] w-[30px]" />,
  },
  {
    title: "friends",
    link: "/friends",
    icon: <FriendsIcon />,
  },
  {
    title: "profile",
    link: "/profile",
    icon: <UserIcon />,
  },
]

export const MainMenu = forwardRef<HTMLDivElement>((_, ref) => {
  const { t } = useTranslation("Navbar")
  const isDark = useDimension((state) => state.isDark)
  const location = useLocation()
  const bottomSafeArea = useDimension((state) => state.bottomSafeArea)
  const posthog = usePostHog()

  const handleNavClick = (item: typeof menuItems[0]) => {
    // Track navigation events
    switch (item.link) {
      case "/profile":
        posthog?.capture(AnalyticsEvent.OpenedProfile)
        break
      case "/customs":
        posthog?.capture(AnalyticsEvent.OpenedCustoms)
        break
      case "/quests":
        posthog?.capture(AnalyticsEvent.OpenedQuests)
        break
      case "/friends":
        posthog?.capture(AnalyticsEvent.OpenedFriends)
        break
      case "/":
        posthog?.capture(AnalyticsEvent.BackToHome)
        break
    }
  }

  return (
    <nav
      ref={ref}
      className="animate-fade-in z-30 fixed bottom-0 left-0 border-hint/20 bg-card flex w-full justify-between rounded-tl-3xl rounded-tr-3xl border-t px-3 pt-2.5"
      style={{ paddingBottom: bottomSafeArea }}
    >
      {menuItems.map((item, index) => (
        <React.Fragment key={item.title}>
          <Link
            className={`text-hint flex w-full flex-col items-center gap-1 text-[10px] pb-3 text-xs leading-3 ${item.title === "home" && "gap-2"} ${location.pathname === item.link && (isDark ? "brightness-150" : "brightness-50")}`}
            to={item.link}
            onClick={() => handleNavClick(item)}
          >
            <div className="relative flex h-[28px] w-[28px] justify-center">
              {item.icon}
            </div>
            {t(item.title)}
          </Link>
          {index < menuItems.length - 1 && (
            <Separator className="bg-hint/50" orientation="vertical" />
          )}
        </React.Fragment>
      ))}
    </nav>
  )
})
